import React, { FC, useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { isEmpty } from "lodash";
import { fileSizeFormat, useDialogShadow } from "@evergis/ui";
import { FileUploader } from "components/Dialogs/DialogComponents/FileUploader/FileUploader";
import { useCheckLimits } from "../../../../hooks/useCheckLimits";

import { DialogActions } from "../../DialogComponents/DialogActions";
import { DialogTitle } from "../../DialogComponents/DialogTitle";
import { FullScreenDialog } from "../../DialogComponents/FullScreenDialog";
import { DialogContent, RestrictedWidthContainer } from "../../DialogComponents/styled";
import { ImportDescription } from "../styled";
import { useImportContext } from "../importContext";
import { useContext } from "../hooks/useContext";
import { useFileLoader } from "../hooks/useFileLoader";
import { useDataSchema } from "../hooks/useDataSchema";
import { SchemaParsed } from "../../../../evergis/core/import/parseSchema";
import { ImportType } from "../types";

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
export const DEFAULT_IMPORT_MAX_FILE_SIZE = 50 * 1024 * 1024;

// eslint-disable-next-line max-lines-per-function
export const ImportFile: FC = () => {
  const { t } = useTranslation("dialogs");
  const context = useContext();
  const { shadows, setScrollContainer } = useDialogShadow();
  const importRef = useRef();
  const { limits } = useCheckLimits();

  const importMaxFileSize = limits?.maxUploadContentSize || DEFAULT_IMPORT_MAX_FILE_SIZE;
  const {
    nextStep,
    setDataSchema,
    file: contextFile,
    setFile,
    setImportType,
    setObjects,
    setAttributes,
    setGeometryType,
    importedFiles,
    setImportedFiles,
    setFileType,
  } = useImportContext();
  const [file, { upload, uploadFileURL }] = useFileLoader();
  const { dataSchema, fileUploaded } = useDataSchema(file);

  const importFile = useCallback(
    (newFiles: File) => {
      if (newFiles[0]) {
        upload(newFiles[0], true);
        setImportedFiles([
          {
            name: newFiles[0].name,
            id: newFiles[0].name,
            total: newFiles[0].size,
            progress: 0,
            done: false,
            error: newFiles[0].size > importMaxFileSize && importMaxFileSize !== -1 ? t("import.fileSizeExceeded") : "",
          },
        ]);
        setObjects(undefined);
        setAttributes(undefined);
        setGeometryType(undefined);
      }
    },
    [setAttributes, setGeometryType, setImportedFiles, setObjects, upload, importMaxFileSize, t],
  );

  const importFileURL = useCallback(
    async fileInfo => {
      const { fileName, contentLength } = fileInfo;

      setImportedFiles([
        {
          name: fileName,
          id: fileName,
          total: contentLength,
          progress: 0,
          done: false,
          error: contentLength > importMaxFileSize && importMaxFileSize !== -1 ? t("import.fileSizeExceeded") : "",
        },
      ]);

      uploadFileURL(fileInfo, fileInfo.url, true);

      setObjects(undefined);
      setAttributes(undefined);
      setGeometryType(undefined);
    },
    [setAttributes, setGeometryType, setImportedFiles, setObjects, uploadFileURL, importMaxFileSize, t],
  );

  const onDelete = useCallback(() => {
    setImportedFiles([]);
  }, [setImportedFiles]);

  const loadFileTypes = useMemo(
    () => [
      {
        name: "Shapefile",
        description: t("import.shapeDescription"),
      },
      {
        name: "Excel",
        description: t("import.excelDescription"),
      },
      {
        name: "GeoJson",
        description: t("import.geoJsonDescription"),
      },
      {
        name: "Keyhole Markup Language (KML)",
        description: t("import.kmlDescription"),
      },
      {
        name: "GDB",
        description: t("import.gdbDescription"),
      },
    ],
    [t],
  );

  useEffect(() => {
    if (fileUploaded) {
      const isArray = Array.isArray(dataSchema);
      const fileType = isArray ? dataSchema[0].fileType : (dataSchema as SchemaParsed).fileType;

      setFile(file);
      setDataSchema(dataSchema);
      setFileType(fileType);

      switch (fileType) {
        case "csv":
        case "xlsx":
          setImportType(ImportType.XLSX);
          break;
        case "kmz":
        case "kml":
          setImportType(ImportType.KML);
          break;
        case "gdb":
          setImportType(ImportType.GDB);
          break;
        default:
          setImportType(ImportType.SHAPE);
          break;
      }

      setImportedFiles(
        (dataSchema as SchemaParsed[])?.length || (dataSchema as SchemaParsed)?.sourceName
          ? [
              {
                ...importedFiles[0],
                progress: importedFiles[0].total,
                done: true,
                error:
                  importedFiles[0].total > importMaxFileSize && importMaxFileSize !== -1
                    ? t("import.fileSizeExceeded")
                    : "",
              },
            ]
          : [
              {
                ...importedFiles[0],
                error: t("import.emptyImportError"),
              },
            ],
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileUploaded]);

  return (
    <FullScreenDialog dialogContext={context}>
      <DialogTitle displayShadow={shadows.upper} />
      <DialogContent ref={setScrollContainer} style={{ paddingTop: 0 }}>
        <RestrictedWidthContainer>
          <ImportDescription>
            {importMaxFileSize !== -1 && (
              <>
                {t("import.canImportNotMoreThan")}
                <strong>{fileSizeFormat(importMaxFileSize)}</strong>.<br />
              </>
            )}
            {t("import.fileTypesSupported")}{" "}
            {loadFileTypes.map(({ name, description }) => (
              <abbr key={name} title={description}>
                {name}
              </abbr>
            ))}
          </ImportDescription>
          <FileUploader
            currentRef={importRef}
            showTitle={!importedFiles[0]}
            fileItems={importedFiles}
            width={"calc(100% - 4rem)"}
            minHeight={"7rem"}
            accept={".xlsx,.csv,.zip,.json,.geojson,.kml,.kmz,.tiff,.tif"}
            isMultiple
            onUpload={importFile}
            onDelete={onDelete}
            onFileUrlUpload={importFileURL}
          />
        </RestrictedWidthContainer>
      </DialogContent>
      <DialogActions
        displayShadow={shadows.bottom}
        applyDisabled={!contextFile || isEmpty(importedFiles) || importedFiles?.some(item => item.error)}
        applyText={t("actions.next", { ns: "common" })}
        onApply={nextStep}
      />
    </FullScreenDialog>
  );
};
